/**
 * Indexing and Cache Service
 * Manages project indexing, thumbnails, and caching for offline-first operation
 * Uses OPFS (Origin Private File System) and IndexedDB for storage
 */

class IndexingService {
  constructor() {
    this.dbName = 'AEDataManagerDB';
    this.dbVersion = 1;
    this.db = null;
    this.isOPFSSupported = this.checkOPFSSupport();
  }

  /**
   * Check if OPFS is supported
   * @returns {boolean} - OPFS support status
   */
  checkOPFSSupport() {
    return 'storage' in navigator && 'getDirectory' in navigator.storage;
  }

  /**
   * Initialize IndexedDB
   * @returns {Promise<IDBDatabase>} - Database instance
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object stores for different types of data
        if (!db.objectStoreNames.contains('projectIndex')) {
          db.createObjectStore('projectIndex', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('thumbnails')) {
          db.createObjectStore('thumbnails', { keyPath: 'filePath' });
        }

        if (!db.objectStoreNames.contains('fileCache')) {
          db.createObjectStore('fileCache', { keyPath: 'filePath' });
        }

        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Create or get project index
   * @param {string} projectId - Project identifier (directory handle name)
   * @param {Array} files - Array of file items
   * @returns {Promise<Object>} - Project index
   */
  async createProjectIndex(projectId, files) {
    if (!this.db) await this.initDB();

    const index = {
      id: projectId,
      createdAt: new Date(),
      updatedAt: new Date(),
      fileCount: files.length,
      files: files.map(file => ({
        name: file.name,
        path: file.path,
        size: file.size,
        lastModified: file.lastModified,
        extension: file.extension,
        type: file.type,
        indexed: false,
        thumbnailGenerated: false
      })),
      statistics: this.calculateStatistics(files)
    };

    // Store in IndexedDB
    const transaction = this.db.transaction(['projectIndex'], 'readwrite');
    const store = transaction.objectStore('projectIndex');
    await store.put(index);

    return index;
  }

  /**
   * Get project index
   * @param {string} projectId - Project identifier
   * @returns {Promise<Object|null>} - Project index or null
   */
  async getProjectIndex(projectId) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['projectIndex'], 'readonly');
      const store = transaction.objectStore('projectIndex');
      const request = store.get(projectId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update project index
   * @param {string} projectId - Project identifier
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} - Updated index
   */
  async updateProjectIndex(projectId, updates) {
    const index = await this.getProjectIndex(projectId);
    if (!index) throw new Error('Project index not found');

    const updatedIndex = {
      ...index,
      ...updates,
      updatedAt: new Date()
    };

    const transaction = this.db.transaction(['projectIndex'], 'readwrite');
    const store = transaction.objectStore('projectIndex');
    await store.put(updatedIndex);

    return updatedIndex;
  }

  /**
   * Calculate project statistics
   * @param {Array} files - Array of file items
   * @returns {Object} - Statistics object
   */
  calculateStatistics(files) {
    const stats = {
      totalFiles: files.length,
      totalSize: 0,
      fileTypes: {},
      extensions: {}
    };

    files.forEach(file => {
      stats.totalSize += file.size || 0;

      const extension = file.extension || 'unknown';
      stats.extensions[extension] = (stats.extensions[extension] || 0) + 1;

      // Categorize file types
      if (['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'].includes(extension)) {
        stats.fileTypes.images = (stats.fileTypes.images || 0) + 1;
      } else if (['.json'].includes(extension)) {
        stats.fileTypes.json = (stats.fileTypes.json || 0) + 1;
      } else if (['.txt', '.md', '.tsv'].includes(extension)) {
        stats.fileTypes.text = (stats.fileTypes.text || 0) + 1;
      } else if (['.glb', '.gltf'].includes(extension)) {
        stats.fileTypes.models = (stats.fileTypes.models || 0) + 1;
      } else {
        stats.fileTypes.other = (stats.fileTypes.other || 0) + 1;
      }
    });

    return stats;
  }

  /**
   * Generate and store thumbnail for image file
   * @param {string} filePath - File path
   * @param {string} imageUrl - Image URL or blob
   * @param {string} projectId - Project identifier
   * @returns {Promise<string>} - Thumbnail URL
   */
  async generateThumbnail(filePath, imageUrl, projectId) {
    try {
      // Create canvas for thumbnail generation
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      // Set thumbnail size (max 200x200)
      const maxSize = 200;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and resize image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      const thumbnailBlob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.8);
      });

      // Store thumbnail in IndexedDB
      const thumbnailData = {
        filePath,
        projectId,
        thumbnail: thumbnailBlob,
        generatedAt: new Date(),
        originalSize: { width: img.width, height: img.height },
        thumbnailSize: { width, height }
      };

      if (!this.db) await this.initDB();
      const transaction = this.db.transaction(['thumbnails'], 'readwrite');
      const store = transaction.objectStore('thumbnails');
      await store.put(thumbnailData);

      // Return object URL for immediate use
      return URL.createObjectURL(thumbnailBlob);
    } catch (error) {
      console.error(`Failed to generate thumbnail for ${filePath}:`, error);
      return null;
    }
  }

  /**
   * Get thumbnail for file
   * @param {string} filePath - File path
   * @returns {Promise<string|null>} - Thumbnail URL or null
   */
  async getThumbnail(filePath) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['thumbnails'], 'readonly');
      const store = transaction.objectStore('thumbnails');
      const request = store.get(filePath);

      request.onsuccess = () => {
        const result = request.result;
        if (result && result.thumbnail) {
          resolve(URL.createObjectURL(result.thumbnail));
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Cache file content for offline access
   * @param {string} filePath - File path
   * @param {any} content - File content
   * @param {string} projectId - Project identifier
   * @returns {Promise<boolean>} - Success status
   */
  async cacheFileContent(filePath, content, projectId) {
    try {
      const cacheData = {
        filePath,
        projectId,
        content,
        cachedAt: new Date(),
        contentType: typeof content
      };

      if (!this.db) await this.initDB();
      const transaction = this.db.transaction(['fileCache'], 'readwrite');
      const store = transaction.objectStore('fileCache');
      await store.put(cacheData);

      return true;
    } catch (error) {
      console.error(`Failed to cache file content for ${filePath}:`, error);
      return false;
    }
  }

  /**
   * Get cached file content
   * @param {string} filePath - File path
   * @returns {Promise<any|null>} - Cached content or null
   */
  async getCachedFileContent(filePath) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['fileCache'], 'readonly');
      const store = transaction.objectStore('fileCache');
      const request = store.get(filePath);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.content : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Store metadata
   * @param {string} key - Metadata key
   * @param {any} value - Metadata value
   * @returns {Promise<boolean>} - Success status
   */
  async setMetadata(key, value) {
    try {
      const metadata = {
        key,
        value,
        updatedAt: new Date()
      };

      if (!this.db) await this.initDB();
      const transaction = this.db.transaction(['metadata'], 'readwrite');
      const store = transaction.objectStore('metadata');
      await store.put(metadata);

      return true;
    } catch (error) {
      console.error(`Failed to set metadata for ${key}:`, error);
      return false;
    }
  }

  /**
   * Get metadata
   * @param {string} key - Metadata key
   * @returns {Promise<any|null>} - Metadata value or null
   */
  async getMetadata(key) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['metadata'], 'readonly');
      const store = transaction.objectStore('metadata');
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all cached data for a project
   * @param {string} projectId - Project identifier
   * @returns {Promise<boolean>} - Success status
   */
  async clearProjectCache(projectId) {
    try {
      if (!this.db) await this.initDB();

      // Clear project index
      const indexTransaction = this.db.transaction(['projectIndex'], 'readwrite');
      const indexStore = indexTransaction.objectStore('projectIndex');
      await indexStore.delete(projectId);

      // Clear thumbnails for this project
      const thumbnailTransaction = this.db.transaction(['thumbnails'], 'readwrite');
      const thumbnailStore = thumbnailTransaction.objectStore('thumbnails');
      const thumbnailRequest = thumbnailStore.index('projectId').openCursor(IDBKeyRange.only(projectId));
      
      await new Promise((resolve) => {
        thumbnailRequest.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
      });

      // Clear file cache for this project
      const cacheTransaction = this.db.transaction(['fileCache'], 'readwrite');
      const cacheStore = cacheTransaction.objectStore('fileCache');
      const cacheRequest = cacheStore.index('projectId').openCursor(IDBKeyRange.only(projectId));
      
      await new Promise((resolve) => {
        cacheRequest.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
      });

      return true;
    } catch (error) {
      console.error(`Failed to clear cache for project ${projectId}:`, error);
      return false;
    }
  }

  /**
   * Get storage usage statistics
   * @returns {Promise<Object>} - Storage usage info
   */
  async getStorageUsage() {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        return {
          quota: estimate.quota,
          usage: estimate.usage,
          usageDetails: estimate.usageDetails || {},
          available: (estimate.quota || 0) - (estimate.usage || 0)
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to get storage usage:', error);
      return null;
    }
  }

  /**
   * Rebuild index from current files
   * @param {string} projectId - Project identifier
   * @param {Array} currentFiles - Current file list
   * @returns {Promise<Object>} - Updated index
   */
  async rebuildIndex(projectId, currentFiles) {
    // Clear existing cache for this project
    await this.clearProjectCache(projectId);

    // Create new index
    const newIndex = await this.createProjectIndex(projectId, currentFiles);

    // Generate thumbnails for image files
    const imageFiles = currentFiles.filter(file => 
      ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'].includes(file.extension?.toLowerCase())
    );

    for (const file of imageFiles) {
      try {
        // This would be called after loading the file content
        // The actual thumbnail generation would happen in the file loading process
        console.log(`Will generate thumbnail for ${file.name}`);
      } catch (error) {
        console.error(`Failed to process ${file.name} for thumbnail:`, error);
      }
    }

    return newIndex;
  }
}

export default IndexingService;